import { useEffect, useRef } from 'react'
import {
  Boxes,
  Cloud,
  GitPullRequest,
  ShieldCheck,
  Activity,
  ArrowRight,
  type LucideIcon,
} from 'lucide-react'
import SectionHeading from './SectionHeading'
import { FOCUS_AREAS } from '../data/focus'
import { initGSAP, prefersReducedMotion } from '../lib/motion'

const ICONS: LucideIcon[] = [Boxes, Cloud, GitPullRequest, ShieldCheck, Activity]

const FocusAreas: React.FC = () => {
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (prefersReducedMotion()) return
    const gsapApi = initGSAP()
    const ctx = gsapApi.context(() => {
      const triggerOpts = { trigger: ref.current, start: 'top 78%', once: true }

      gsapApi.fromTo(
        '[data-focus-row]',
        { opacity: 0, y: 24 },
        {
          opacity: 1,
          y: 0,
          duration: 0.6,
          ease: 'power3.out',
          stagger: 0.08,
          scrollTrigger: triggerOpts,
        }
      )

      gsapApi.fromTo(
        '[data-focus-index]',
        { x: -14, opacity: 0 },
        {
          x: 0,
          opacity: 1,
          duration: 0.5,
          ease: 'power2.out',
          stagger: 0.08,
          scrollTrigger: triggerOpts,
        }
      )

      gsapApi.fromTo(
        '[data-focus-icon]',
        { scale: 0.9, opacity: 0 },
        {
          scale: 1,
          opacity: 1,
          duration: 0.45,
          ease: 'power2.out',
          stagger: 0.08,
          scrollTrigger: triggerOpts,
        }
      )
    }, ref)
    return () => ctx.revert()
  }, [])

  return (
    <section id="focus" ref={ref} className="section-padding relative">
      <div className="container-width">
        <SectionHeading
          label="Focus areas"
          meta="daily work"
          title={
            <>
              What I <span className="text-gradient">focus on</span>
            </>
          }
          description="The disciplines I design, build and operate across — from cloud infrastructure to the security and observability rails around it."
        />

        <div className="gradient-border overflow-hidden rounded-2xl bg-white shadow-soft dark:bg-neutral-900">
          {FOCUS_AREAS.map((area, i) => {
            const Icon = ICONS[i]
            return (
              <div
                key={area.title}
                data-focus-row
                className="group border-t border-neutral-200/70 first:border-t-0 dark:border-white/8 transition-colors duration-300 hover:bg-neutral-50/80 dark:hover:bg-white/[0.03]"
              >
                <div className="flex flex-col gap-4 px-6 py-7 sm:px-8 md:flex-row md:items-start md:gap-8 md:py-8">
                  <div className="flex items-center gap-4 md:w-16 md:shrink-0 md:flex-col md:items-start md:gap-3 md:pt-0.5">
                    <span
                      data-focus-index
                      className="font-mono text-xs uppercase tracking-[0.2em] text-neutral-400 transition-colors duration-300 group-hover:text-primary-500 dark:text-neutral-500 dark:group-hover:text-primary-400"
                    >
                      {String(i + 1).padStart(2, '0')}
                    </span>
                    <span
                      data-focus-icon
                      className="flex w-11 h-11 shrink-0 items-center justify-center rounded-xl bg-primary-500/10 text-primary-500 transition-all duration-300 group-hover:bg-primary-500/15 group-hover:shadow-glow"
                    >
                      {Icon && <Icon size={20} />}
                    </span>
                  </div>

                  <div className="min-w-0">
                    <h3 className="font-display text-xl font-semibold tracking-tight transition-colors duration-300 group-hover:text-primary-600 dark:group-hover:text-primary-400 sm:text-2xl">
                      {area.title}
                      <span
                        aria-hidden="true"
                        className="inline-flex w-5 h-5 -translate-x-1 items-center justify-center align-middle opacity-0 transition-all duration-300 group-hover:translate-x-0 group-hover:opacity-100"
                      >
                        <ArrowRight
                          size={15}
                          className="text-primary-500 dark:text-primary-400"
                        />
                      </span>
                    </h3>
                    <p className="mt-2 max-w-3xl text-sm leading-relaxed text-neutral-600 dark:text-neutral-400">
                      {area.description}
                    </p>

                    <div className="mt-4 flex flex-wrap gap-2">
                      {area.tags.map((tag) => (
                        <span
                          key={tag}
                          className="inline-flex items-center rounded-md border border-neutral-200 px-2.5 py-1 font-mono text-xs text-neutral-500 transition-colors duration-300 group-hover:border-primary-300/50 dark:border-white/8 dark:text-neutral-400 dark:group-hover:border-primary-400/40"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </section>
  )
}

export default FocusAreas